import { useState, type FormEvent } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  AdminTag,
  AppShell,
  Button,
  Crumb,
  EnvTag,
  GpNav,
  Input,
  Skeleton,
  TopBar,
  type NavItemSpec,
  type ThemeName,
} from "@guestpilot/ui-kit";
import {
  ArrowTrendingLines20Regular,
  Building20Regular,
  Flag20Regular,
  History20Regular,
  Money20Regular,
  PersonAdd20Regular,
  Tag20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from "@fluentui/react-icons";
import { ApiProblemError, useGet } from "./api";
import { useAuth } from "./auth";
import { ENV } from "./env";
import { AuditLog } from "./pages/AuditLog";
import { Billing } from "./pages/Billing";
import { CustomerDetail } from "./pages/CustomerDetail";
import { Customers } from "./pages/Customers";
import { Dashboard } from "./pages/Dashboard";
import { Enrollment } from "./pages/Enrollment";
import { FeatureFlags } from "./pages/FeatureFlags";
import { Plans } from "./pages/Plans";
import { SignIn } from "./pages/SignIn";

interface Me {
  id: string;
  name: string;
  email: string;
  environment: "production" | "staging";
}

const NAV: NavItemSpec[] = [
  { path: "/", label: "Dashboard", icon: <ArrowTrendingLines20Regular /> },
  { path: "/customers", label: "Customers", icon: <Building20Regular /> },
  { path: "/plans", label: "Plans & pricing", icon: <Tag20Regular /> },
  { path: "/flags", label: "Feature flags", icon: <Flag20Regular /> },
  { path: "/billing", label: "Billing", icon: <Money20Regular /> },
  { path: "/enrollment", label: "Enrollment", icon: <PersonAdd20Regular /> },
  { path: "/audit", label: "Audit log", icon: <History20Regular /> },
];

const ENV_LABEL = { production: "Production", staging: "Staging", dev: "Development" } as const;

export function pageTitle(path: string): string {
  const hit = [...NAV]
    .reverse()
    .find((n) => (n.path === "/" ? path === "/" : path === n.path || path.startsWith(`${n.path}/`)));
  return hit?.label ?? "Dashboard";
}

/**
 * Not signed in → sign-in screen. Signed in but refused by the API (another tenant, not provisioned: BR-241) → the same screen
 * with the error inline. Every account that gets in has the same access (D-040), so there are no route guards by role.
 */
export function App({ theme, onToggleTheme }: { theme: ThemeName; onToggleTheme: () => void }) {
  const auth = useAuth();
  if (!auth.ready) return <Skeleton rows={3} />;
  if (!auth.staff) return <SignIn />;
  return <Authenticated theme={theme} onToggleTheme={onToggleTheme} />;
}

function Authenticated({ theme, onToggleTheme }: { theme: ThemeName; onToggleTheme: () => void }) {
  const auth = useAuth();
  const me = useGet<Me>("/me");
  if (ENV.tenantId && auth.staff && auth.staff.tenantId.toLowerCase() !== ENV.tenantId.toLowerCase()) {
    return (
      <SignIn error="This account belongs to another organisation. Sign in with your GuestPilot account." />
    );
  }
  if (me.error instanceof ApiProblemError && (me.error.status === 401 || me.error.status === 403)) {
    return (
      <SignIn
        error={
          me.error.status === 403
            ? "This account is not provisioned for GuestPilot Admin. Ask the platform owner for access."
            : "This account could not be verified. Sign in with your GuestPilot account."
        }
      />
    );
  }
  if (me.isLoading || !me.data) return <Skeleton rows={3} />;
  return <Shell me={me.data} theme={theme} onToggleTheme={onToggleTheme} />;
}

function Shell({ me, theme, onToggleTheme }: { me: Me; theme: ThemeName; onToggleTheme: () => void }) {
  const auth = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const search = (e: FormEvent) => {
    e.preventDefault();
    nav(q.trim() ? `/customers?q=${encodeURIComponent(q.trim())}` : "/customers");
  };
  return (
    <AppShell
      nav={
        <GpNav
          brand="GuestPilot"
          label="Admin navigation"
          skipLabel="Skip to content"
          items={NAV}
          active={loc.pathname}
          onNavigate={nav}
          footer={
            <div className="gp-stack">
              <AdminTag>ADMIN</AdminTag>
              <Button variant="ghost" onClick={() => void auth.signOut()}>
                Sign out
              </Button>
            </div>
          }
        />
      }
      topBar={
        <TopBar
          left={
            <>
              <Crumb root="Admin" page={pageTitle(loc.pathname)} />
              <form onSubmit={search} role="search" aria-label="Search customers">
                <Input
                  type="search"
                  aria-label="Search customers by name or domain"
                  placeholder="Search customers"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </form>
              <EnvTag environment={me.environment} label={ENV_LABEL[me.environment]} />
              <Button
                variant="ghost"
                aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                onClick={onToggleTheme}
              >
                {theme === "dark" ? (
                  <WeatherSunny20Regular aria-hidden />
                ) : (
                  <WeatherMoon20Regular aria-hidden />
                )}
              </Button>
            </>
          }
          person={{ name: me.name }}
        />
      }
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:organizationId" element={<CustomerDetail />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/flags" element={<FeatureFlags />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/enrollment" element={<Enrollment />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
